import {MaterialSource, MaterialType} from '@domain/materials'
import {dataTestId} from '@hconnect/uikit'
import {Card} from '@hconnect/uikit/src/lib2'
import {
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material'
import classNames from 'classnames'
import React, {createRef, useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useSearchParams} from 'react-router'

import {saveLastSelectedMaterialType} from '../../helpers/lastSelectedMaterial'
import {useMaterialsQuery} from '../../hooks'
import {
  isBoughtFromVendorOrBoughtAndProduced,
  isProducedInPlantOrBoughtAndProduced
} from '../materials-tabs/general/MaterialSourceInputs'
import {FilterOptions} from '../materials-tree/MaterialsTreeFilters'

import {ClassificationRow} from './classificationRows/ClassificationRow'
import {MaterialsListTableHeader} from './MaterialsListTableHeader'
import {useTableStyles} from './tableStyles'

import {FIRST_COLUMN_WIDTH} from '@settings/modules/common/consts'
import {useUrlParam} from '@settings/modules/routing'

const TrimmedText = styled(Typography)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block'
})

export const MaterialsList: React.FC = () => {
  const materialType = useUrlParam('materialType')
  const plantCode = useUrlParam('plantCode')
  const {t} = useTranslation()
  const {classes} = useTableStyles()
  const [searchParams] = useSearchParams()
  const filter = searchParams.get('filter') as FilterOptions | null
  const filterText = searchParams.get('filterText')?.toLowerCase() || ''

  const {data: materialsForType} = useMaterialsQuery(materialType as MaterialType)

  // Save to localStorage when user views a type's table (no specific material selected)
  useEffect(() => {
    if (materialType && plantCode) {
      saveLastSelectedMaterialType(plantCode, materialType)
    }
  }, [materialType, plantCode])

  const [sortKey, setSortKey] = useState<string>('name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const [isScrolledToRight, setIsScrolledToRight] = useState(false)
  const tableContainerRef = createRef<HTMLDivElement>()

  const handleScroll = useCallback((event) => {
    const target = event.target as HTMLDivElement
    setIsScrolledToRight(target.scrollLeft > 0)
  }, [])

  const getMaterialSource = useCallback(
    (materialSource: MaterialSource) => {
      const parts: string[] = []

      if (isProducedInPlantOrBoughtAndProduced(materialSource)) {
        parts.push(t('materialsSettings.producedInPlant'))
      }

      if (isBoughtFromVendorOrBoughtAndProduced(materialSource)) {
        parts.push(t('materialsSettings.receivedFromVendor'))
      }

      return parts.join(', ')
    },
    [t]
  )

  const handleSort = useCallback(
    (key: string) => {
      setSortKey(key)
      setSortDirection((prevDirection) => {
        const isSameKey = sortKey === key
        return isSameKey && prevDirection === 'asc' ? 'desc' : 'asc'
      })
    },
    [sortKey]
  )

  const filteredAndSortedData = useMemo(() => {
    let data = materialsForType || []

    if (filter && filter !== FilterOptions.All) {
      data = data.filter((material) => {
        const source = material.source
        if (filter === FilterOptions.Produced && isProducedInPlantOrBoughtAndProduced(source)) {
          return true
        }
        return filter === FilterOptions.Received && isBoughtFromVendorOrBoughtAndProduced(source)
      })
    }

    if (filterText) {
      data = data.filter((material) =>
        [material.name, material.globalName]
          .filter(Boolean)
          .some((value) => value?.toLowerCase().includes(filterText))
      )
    }

    return [...data].sort((a, b) => {
      const aVal = a[sortKey]?.toString().toLowerCase() || ''
      const bVal = b[sortKey]?.toString().toLowerCase() || ''
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
    })
  }, [materialsForType, sortKey, sortDirection, filter, filterText])

  return (
    <Card>
      <TableContainer
        component={Paper}
        onScroll={handleScroll}
        ref={tableContainerRef}
        style={{height: 'calc(100vh - 315px)'}}
      >
        <Table
          style={{width: '100%'}}
          stickyHeader
          {...dataTestId(`materials-list-table.${materialType}`)}
        >
          <MaterialsListTableHeader
            materialType={materialType as MaterialType}
            showFirstColumnShadow={isScrolledToRight}
            onSort={handleSort}
            sortKey={sortKey}
            sortDirection={sortDirection}
          />
          <TableBody>
            {filteredAndSortedData?.map((material) => (
              <TableRow key={material.id}>
                <TableCell
                  sx={{maxWidth: FIRST_COLUMN_WIDTH}}
                  className={classNames(
                    classes.stickyFirstColumn,
                    isScrolledToRight && classes.tableCellRightShadow
                  )}
                >
                  <TrimmedText
                    sx={{
                      maxWidth: FIRST_COLUMN_WIDTH
                    }}
                    variant="caption"
                  >
                    {material.name}
                  </TrimmedText>
                </TableCell>
                <TableCell>
                  <TrimmedText
                    sx={{
                      maxWidth: FIRST_COLUMN_WIDTH
                    }}
                    variant="caption"
                  >
                    {material.description}
                  </TrimmedText>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{material.globalName}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{getMaterialSource(material.source)}</Typography>
                </TableCell>
                <ClassificationRow material={material} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  )
}
